import React, { useEffect, useState } from "react";

import { Card, Button, Row, Form, Col } from "react-bootstrap";
import Select, { ActionMeta } from "react-select";

import * as APIModels from "../../../../models";
import { TylerDataService } from "../../../../services/DataService";

import { Globals } from "../../../../helpers/globals";
import { authenticationService } from "../../../../services/authentication.service";
type MyOptionType = { label: string; value: string };

/**
 * Creates a component to display important information about a tyler case.
 *
 * @remarks
 * This component is used for both new cases and existing cases. It is dependent
 * on whether or not `saveCaseData` is passed into the component or not.
 *
 * @param caseData      - if the caseData is passed in and is not null, then the case
 *                        is considered existing.
 * @param saveCaseData  - if the saveCaseData is not null then the case is considered
 *                        as a new case. It should be a callback function to be used
 *                        to send the `APIModel.Submission` with proper data.
 *
 * @throws {Error}
 * Thrown if both caseData and saveCaseData is thrown.
 */
export default function CaseInformationCard({
    caseData,
    saveCaseData,
    isNewCase,
}: {
    caseData?: APIModels.CaseData;
    saveCaseData?: (cd: APIModels.CaseData) => void;
    isNewCase?: boolean;
}) {
    let options: MyOptionType[] = [];

    const [errorMessage, setErrorMessage] = useState("");

    const [completedCaseData, setCompletedCaseData] = useState(
        caseData ?? new APIModels.CaseData()
    );

    const [counties, setCounties] = useState([] as APIModels.County[]);
    const [countyName, setCountyName] = useState("");
    const [divisionName, setDivisionName] = useState("");
    const [caseTypeName, setCaseTypeName] = useState("");

    const [countyOptions, setCountyOptions] = useState([] as MyOptionType[]);
    const [divisionOptions, setDivisionOptions] = useState(
        [] as MyOptionType[]
    );
    const [caseTypeOptions, setCaseTypeOptions] = useState(
        [] as MyOptionType[]
    );
    const [caseSubTypeOptions, setCaseSubTypeOptions] = useState(options);
    const [damageCodeOptions, setDamageCodeOptions] = useState(options);
    const [remedyOptions, setRemedyOptions] = useState(options);

    useEffect(() => {
        if (caseData) {
            setCompletedCaseData(caseData);
        }
        TylerDataService.getCountiesByPortal({ state: portal! }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    if (caseData?.countyID === v.id) {
                        setCountyName(v.name!);
                    }
                    return { value: v.id!, label: v.name! };
                });
                setCountyOptions(options);
                setCounties(resp);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [caseData]);

    const onSaveClick = () => {
        console.log(completedCaseData.division);
        if (saveCaseData) saveCaseData(completedCaseData);
    };

    const portal = authenticationService.portalValue;

    useEffect(() => {
        console.log("Brett " + portal);
        TylerDataService.getCountiesByPortal({ state: portal! }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    return { value: v.id!, label: v.name! };
                });
                setCountyOptions(options);
                setCounties(resp);
                return options;
            },
            (error) => {
                console.log(error);
            }
        );
    }, []);

    useEffect(() => {
        if (
            completedCaseData.countyID &&
            completedCaseData.countyID != APIModels.EmptyGuid
        ) {
            if (!isNewCase) {
                TylerDataService.getCountyByID({
                    countyId: completedCaseData.countyID!,
                }).then(
                    (resp) => {
                        setCountyName(resp.name!);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            } else {
                TylerDataService.getDivisions({
                    countyId: completedCaseData.countyID!,
                }).then(
                    (resp) => {
                        options = resp.map((v, i) => {
                            if (caseData?.divisionID == v.id) {
                                setDivisionName(v.name!);
                            }
                            return { value: v.id!, label: v.name! };
                        });
                        setDivisionOptions(options);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            }
        }
    }, [completedCaseData.countyID]);

    useEffect(() => {
        setCaseSubTypeOptions([]);
        if (
            completedCaseData.divisionID &&
            completedCaseData.divisionID != APIModels.EmptyGuid
        ) {
            if (!isNewCase) {
                TylerDataService.getDivisionByID({
                    divisionId: completedCaseData.divisionID!,
                }).then(
                    (resp) => {
                        setDivisionName(resp.name!);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            } else {
                TylerDataService.getCaseTypes({
                    // Production: Uncomment this line
                    divisionId: completedCaseData.divisionID!,
                }).then(
                    (resp) => {
                        console.log(caseData?.caseTypeID);
                        options = resp
                            //.filter((v) => v.active)
                            .map((v, i) => {
                                if (caseData?.caseTypeID == v.id) {
                                    setCaseTypeName(v.description!);
                                }
                                return { value: v.id!, label: v.description! };
                            });
                        setCaseTypeOptions(options);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            }
        }
    }, [completedCaseData.divisionID]);

    useEffect(() => {
        if (
            completedCaseData.caseTypeID &&
            completedCaseData.caseTypeID != APIModels.EmptyGuid
        ) {
            if (!isNewCase) {
                TylerDataService.getCaseTypeByID({
                    caseTypeId: completedCaseData.caseTypeID!,
                }).then(
                    (resp) => {
                        setCaseTypeName(resp.description!);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            } else {
                TylerDataService.getCaseSubTypes({
                    caseTypeId: completedCaseData.caseTypeID!,
                }).then(
                    (resp) => {
                        options = resp.map((v, i) => {
                            return { value: v.id!, label: v.name! };
                        });
                        setCaseSubTypeOptions(options);
                    },
                    (error) => {
                        console.log(error);
                    }
                );
            }
        }
    }, [completedCaseData.caseTypeID]);

    useEffect(() => {
        if (
            completedCaseData.divisionID &&
            completedCaseData.divisionID != APIModels.EmptyGuid
        ) {
            TylerDataService.getDamagesCodes({
                divisionId: completedCaseData.divisionID!,
            }).then(
                (resp) => {
                    options = resp.map((v, i) => {
                        return { value: v.id!, label: v.name! };
                    });
                    setDamageCodeOptions(options);
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    }, [completedCaseData.divisionID]);

    useEffect(() => {
        if (
            completedCaseData.divisionID &&
            completedCaseData.divisionID != APIModels.EmptyGuid
        ) {
            TylerDataService.getRemedyCodes({
                divisionId: completedCaseData.divisionID!,
            }).then(
                (resp) => {
                    options = resp.map((v, i) => {
                        return { value: v.id!, label: v.name! };
                    });
                    setRemedyOptions(options);
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    }, [completedCaseData.divisionID]);
    const onCountyChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            countyID: value?.value,
            county: value?.label,
            divisionID: APIModels.EmptyGuid,
            division: "",
            caseTypeID: APIModels.EmptyGuid,
            caseType: "",
            caseSubTypeID: APIModels.EmptyGuid,
            caseSubType: "",
        });
        document.getElementById("selCounty")?.style.setProperty("border", "");
    };

    const onDivisionChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        console.log(`The division is ${value} in Child`);
        setCompletedCaseData({
            ...completedCaseData,
            divisionID: value?.value,
            division: value?.label,
        });
        document.getElementById("selDivision")?.style.setProperty("border", "");
    };

    const onCaseTypeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            caseTypeID: value?.value,
            caseType: value?.label,
        });
        document.getElementById("selCaseType")?.style.setProperty("border", "");
    };

    const onCaseSubtypeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            caseSubTypeID: value?.value,
            caseSubType: value?.label,
        });
    };

    const onRemedyCodeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            damagesCodeID: value?.value,
        });
    };

    const onDamagesCodeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            damagesCodeID: value?.value,
        });
    };

    const filterOption = (option: MyOptionType, inputValue: string): boolean =>
        (
            option.label
                .toString()
                .toLowerCase()
                .match(inputValue.toLowerCase()) || []
        ).length > 0;

    return (
        <div className="card" style={{ padding: "2rem" }}>
            <div className="card-header">
                <h3>
                    {isNewCase ? "New Case Information" : "Case Information"}
                </h3>
            </div>
            <div
                className="card-body"
                style={{ paddingLeft: "4rem", paddingRight: "4rem" }}
            >
                <div>
                    {isNewCase === false ? (
                        <div>
                            <div>
                                <label>Case Number</label>
                            </div>
                            <div>
                                <Form.Text>
                                    {isNewCase
                                        ? "New Case"
                                        : " " + caseData?.courtCaseNumber ??
                                          caseData?.ucn ??
                                          ""}
                                </Form.Text>
                            </div>
                            <div>
                                <label
                                    style={{
                                        color: "blue",
                                        paddingRight: "3rem",
                                    }}
                                >
                                    Case Title
                                </label>
                            </div>
                            <div>
                                <Form.Text>
                                    {isNewCase
                                        ? "New Case"
                                        : " " + caseData?.caseTitle ?? ""}
                                </Form.Text>
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="row  my-3">
                        <div className="col-4">
                            <label htmlFor="selCounty" className="form-label">
                                County/Court
                            </label>
                            {isNewCase ? (
                                <Select
                                    id="selCounty"
                                    name="selCounty"
                                    style={{ border: "1px solid #ff0000ff" }}
                                    value={countyOptions.filter(
                                        (c) =>
                                            c.value ===
                                            completedCaseData.countyID
                                    )}
                                    filterOption={filterOption}
                                    options={countyOptions}
                                    onChange={onCountyChange}
                                />
                            ) : (
                                <div> {countyName} </div>
                            )}
                        </div>
                        <div className="col-4">
                            <label className="form-label">Category</label>

                            {isNewCase ? (
                                <Select
                                    id="selDivision"
                                    name="selDivision"
                                    value={divisionOptions.filter(
                                        (c) =>
                                            c.value ===
                                            completedCaseData.divisionID
                                    )}
                                    style={{ border: "1px solid #ff0000ff" }}
                                    filterOption={filterOption}
                                    options={divisionOptions}
                                    onChange={onDivisionChange}
                                />
                            ) : (
                                <div>{divisionName}</div>
                            )}
                        </div>

                        <div className="col-4">
                            <label className="form-label">Case Type</label>

                            {isNewCase ? (
                                <Select
                                    id="selCaseType"
                                    name="selCaseType"
                                    value={caseTypeOptions.filter(
                                        (c) =>
                                            c.value ===
                                            completedCaseData.caseTypeID
                                    )}
                                    style={{ border: "1px solid #ff0000ff" }}
                                    filterOption={filterOption}
                                    options={caseTypeOptions}
                                    onChange={onCaseTypeChange}
                                />
                            ) : (
                                <div>{caseTypeName}</div>
                            )}
                        </div>
                    </div>
                    {isNewCase && (
                        <div className="row  mb-3">
                            <div className="col-4">
                                <label className="form-label">
                                    Case Sub-Type
                                </label>
                                <Select
                                    id="selCaseSubtype"
                                    name="selCaseSubtype"
                                    value={caseSubTypeOptions.filter(
                                        (c) =>
                                            c.value ===
                                            completedCaseData.caseSubTypeID
                                    )}
                                    filterOption={filterOption}
                                    options={caseSubTypeOptions}
                                    onChange={onCaseSubtypeChange}
                                />
                            </div>
                            <div className="col-4">
                                <label className="form-label">
                                    Damages Code
                                </label>
                                <Select
                                    id="selDamagesCodes"
                                    name="selDamagesCodes"
                                    value={damageCodeOptions.filter(
                                        (c) =>
                                            c.value ===
                                            completedCaseData.damagesCodeID
                                    )}
                                    filterOption={filterOption}
                                    options={damageCodeOptions}
                                    onChange={onDamagesCodeChange}
                                />
                            </div>

                            <div className="col-4">
                                <label className="form-label">
                                    Remedy Codes
                                </label>
                                <Select
                                    id="selRemedyCodes"
                                    name="selRemedyCodes"
                                    filterOption={filterOption}
                                    options={remedyOptions}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="card-footer">
                {
                    // TODO: Add Save new Case when working on it
                    isNewCase && (
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={onSaveClick}
                        >
                            Save
                        </button>
                    )
                }
            </div>
        </div>
    );
}
